import { DateTime } from "luxon";

export function minutesBetweenDates(
  date1: Date | string,
  date2: Date | string,
) {
  const d1 = new Date(date1).getTime();
  const d2 = new Date(date2).getTime();
  const diffInMs = Math.abs(d2 - d1);
  const minutes = Math.floor(diffInMs / (1000 * 60));
  return minutes;
}

export interface Event {
  type: string;
  scored?: boolean;
  category?: string;
  duration: number;
  category_id?: string;
}

/**
 * Formats a date using Luxon
 * @param date A Date object or date string to format
 * @param format The format string (defaults to 'yyyy-MM-dd')
 * @param locale Optional locale for internationalization
 * @returns Formatted date string
 */
export function formatDate(
  date: Date | string,
  format: string = "MM/dd/yy hh:mm a",
  locale?: string,
): string {
  let dateTime: DateTime;

  if (date instanceof Date) {
    // Convert JavaScript Date to Luxon DateTime
    dateTime = DateTime.fromJSDate(date);
  } else {
    // Parse string to Luxon DateTime
    dateTime = DateTime.fromISO(date);
  }

  // Check if the date is valid
  if (!dateTime.isValid) {
    throw new Error(`Invalid date: ${date}`);
  }

  // Apply locale if provided
  if (locale) {
    dateTime = dateTime.setLocale(locale);
  }

  // Format the date and return
  return dateTime.toFormat(format);
}

/**
 * Finds the current event based on elapsed time since start time
 * @param startTime - ISO string or any format accepted by Date constructor
 * @param events - Array of event objects, each with a duration property
 * @returns The current event object or null if no event is active
 */
export function getCurrentEvent(
  startTime: string,
  events: Event[],
): null | { event: Event | null; index?: number; section?: number } {
  // Convert input string to Date object
  const startTimestamp = new Date(startTime);

  // Calculate elapsed minutes since start_time
  const currentTimestamp = new Date();
  const elapsedMinutes =
    (currentTimestamp.getTime() - startTimestamp.getTime()) / (1000 * 60);

  let currentDuration = 0;
  let section = 1;

  // Iterate through each event
  for (const [index, event] of (events ?? []).entries()) {
    // Get the duration of the current event
    const eventDuration = event.duration;

    // Check if elapsed time falls within this event's window
    if (
      elapsedMinutes >= currentDuration &&
      elapsedMinutes < (currentDuration + eventDuration)
    ) {
      return {
        event,
        index,
        section,
      };
    }

    if (event.type !== "break") {
      section++;
    }

    // Update the cumulative duration
    currentDuration += eventDuration;
  }

  // If elapsed time exceeds total duration of all events, return null
  return null;
}

/**
 * Converts a string from words to snake_case format
 * @param str The input string to convert
 * @returns The converted snake_case string
 */
export function wordToSnakeCase(str: string): string {
  if (!str) return "";

  // Handle camelCase by adding space before uppercase letters
  const withSpaces = str.replace(/([A-Z])/g, " $1");

  // Replace non-alphanumeric characters with spaces
  const normalized = withSpaces.replace(/[^a-zA-Z0-9\s]/g, " ");

  // Convert to lowercase, trim, and replace spaces with underscores
  return normalized
    .toLowerCase()
    .trim()
    .replace(/\s+/g, "_");
}

interface WeightedItem {
  draw_weight: number;
}

export function drawRandomObjects(
  items: WeightedItem[],
  draws: number,
): WeightedItem[] {
  // Input validation
  if (items.length === 0) {
    throw new Error("Items array cannot be empty");
  }

  if (draws < 0) {
    throw new Error("Number of draws must be non-negative");
  }

  // Calculate total weight
  const totalWeight = items.reduce((sum, item) => sum + item.draw_weight, 0);

  if (totalWeight <= 0) {
    throw new Error("Total weight must be positive");
  }

  const results: WeightedItem[] = [];

  for (let i = 0; i < draws; i++) {
    // Generate a random number between 0 and totalWeight
    const randomValue = Math.random() * totalWeight;

    // Find the item based on cumulative weight
    let cumulativeWeight = 0;
    for (const item of items) {
      cumulativeWeight += item.draw_weight;
      if (randomValue < cumulativeWeight) {
        results.push(item);
        break;
      }
    }
  }

  return results;
}

/**
 * Replaces all instances of the pattern [[$1::$2]] with just $1
 * @param text - The input text to process
 * @returns The text with all patterns replaced
 */
export function replaceTaggedValues(text: string): string {
  // Use regex to match the pattern and replace with just the first captured group
  return text.replace(/\[\[(.*?)::(.*?)\]\]/g, "$1");
}
